import React from 'react';
import { gql } from '@apollo/client';
import { Icon } from '../../../../blocks/icons';
import { ButtonBarButtonProps } from '../../../ButtonBar/ButtonBarButton';
import {
	useInvoiceViewButtonBar_ApproveInvoiceMutation,
	InvoiceStatus,
	InvoiceViewButtonBarApproveButtonInvoiceFragment,
} from '#types';
import { ButtonBarButtonsProp } from '../../../ButtonBar';
import { useToast } from '../../../../hooks/useToast';
import { Box } from '@mui/material';
import { CheckCircleFilled } from '../../../../blocks/icons/CheckCircleFilled';
import styles from './useInvoiceViewButtonBarApproveButton.module.scss';
import { useDeviceDetection } from '../../../../hooks/useDeviceDetection';
import rpcShared from '@rockpapercoin/rpc-shared';

export const invoiceViewButtonBarApproveButtonInvoiceFragment = gql`
	fragment invoiceViewButtonBarApproveButtonInvoice on Invoice {
		id
		status
		approvedByPlanner
		contact {
			...getViewingUserDocumentRelationshipContact
		}
	}
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewButtonBar_approveInvoice = gql`
	mutation invoiceViewButtonBar_approveInvoice(
		$where: InvoiceWhereInputWithGuestToken!
	) {
		approveInvoice(where: $where) {
			id
			approvedByPlanner
		}
	}
`;

type UseInvoiceViewButtonBarApproveButtonReturnType = {
	approveButton: ButtonBarButtonsProp['buttons'];
	approvedWidget?: React.ReactNode;
};

type UseInvoiceViewButtonBarApproveButtonProps = {
	invoice?: InvoiceViewButtonBarApproveButtonInvoiceFragment;
	requestingUserBaseId?: string;
	guestToken?: string;
};

export const useInvoiceViewButtonBarApproveButton = ( {
	invoice,
	requestingUserBaseId,
	guestToken,
}: UseInvoiceViewButtonBarApproveButtonProps ): UseInvoiceViewButtonBarApproveButtonReturnType => {
	const { showError, showSuccess } = useToast();

	const [ approveInvoice, { loading } ] =
		useInvoiceViewButtonBar_ApproveInvoiceMutation();

	const isActionable =
		invoice?.status &&
		![
			InvoiceStatus.FullyPaid,
			InvoiceStatus.FullyPaidPartiallyRefunded,
			InvoiceStatus.FullyRefunded,
			InvoiceStatus.PartiallyPaid,
			InvoiceStatus.Void,
			InvoiceStatus.Disputed,
		].includes( invoice?.status );

	const { isMobile } = useDeviceDetection();

	const { viewingUserMayApprove, viewingUserIsCustomer } =
		rpcShared.contacts.getViewingUserDocumentRelationship( {
			contact: invoice?.contact,
			viewingUserId: requestingUserBaseId,
			permission: 'plannerOrgCanPayInvoices',
		} );

	const isVisible =
		viewingUserMayApprove &&
		isActionable &&
		!invoice?.approvedByPlanner &&
		!invoice?.contact?.customer?.clientUser?.plannerOrgCanPayInvoices;

	const onClick = async () => {
		if ( !invoice?.id ) {
			// insulate against the ID being missing, like in slow connections
			return;
		}
		const res = await approveInvoice( {
			variables: {
				where: {
					id: invoice?.id,
					guestToken,
				},
			},
		} );
		if ( res.errors ) {
			res.errors.map( ( error: any ) => showError( error ) );
		} else if ( !res.data?.approveInvoice ) {
			showError();
		} else {
			showSuccess( 'Invoice approved!' );
		}
	};

	const approvedText = !invoice?.approvedByPlanner
		? undefined
		: isMobile
			? 'Approved'
			: viewingUserIsCustomer
				? 'This invoice has been approved by your planner.'
				: 'This invoice has been approved.';

	const approveButton: Record<string, ButtonBarButtonProps['buttonProps']> = {
		APPROVE: {
			isVisible,
			isIconWhenSmall: true,
			variant: 'contained',
			color: 'primary',
			'data-cy': 'approve-button',
			startIcon: <Icon type='check' />,
			onClick,
			loading,
		},
	};

	return {
		approveButton,
		...( approvedText
			? {
				approvedWidget: (
					<Box data-cy='accepted-button' className={ styles.messageButton }>
						<CheckCircleFilled />
						{ approvedText }
					</Box>
				),
			  }
			: {} ),
	};
};
